import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  AccordionHeaderContent,
  AccordionItemContentType,
  DrugItem,
  Filter,
  FilterTypes,
  SelectedFilter
} from 'src/api/types';
import { GeneratedLinkUrlObject, SearchPageQueryType } from 'src/utils/links';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import AccordionDefaultBody from 'src/shared-components/Accordion/AccordionDefaultBody';
import AccordionDefaultHeader from 'src/shared-components/Accordion/AccordionDefaultHeader';
import Accordion from 'src/shared-components/Accordion/Accordion';
import dynamic from 'next/dynamic';
import SkeletonLoaders from 'src/shared-components/Loaders/SkeletonLoaders';
import ButtonSecondary from 'src/shared-components/design-system/ButtonSecondary';
import cn from 'classnames';
import useGTAG from '../../../hooks/useGTAG';
import { querifiedFilters } from '../utils';
import trns from '../trns';
import FilterComponent from './FilterComponent';
import FilterCard from './FilterCard';

const RangeFilter = dynamic(() => import('./RangeFilter'), {
  ssr: false
});

interface Props {
  filters: Filter[];
  selectedFilters: SelectedFilter[];
  drugList: DrugItem[];
  isLoading: boolean;
  pageLinkGenerationMethod: (
    query: SearchPageQueryType
  ) => GeneratedLinkUrlObject;
  onSubmitFilters?: () => void;
  textSearch?: string;
  containerClassName?: string;
}

const FiltersContainer: FC<Props> = ({
  filters,
  selectedFilters,
  isLoading,
  drugList,
  pageLinkGenerationMethod,
  onSubmitFilters,
  textSearch,
  containerClassName
}) => {
  const router = useRouter();
  const [currentFilters, setCurrentFilters] = useState<Filter[]>(filters);
  const [allFilters, setAllFilters] = useState<Filter[]>(filters);
  const [submittedPriceFilter, setSubmittedPriceFilter] =
    useState<Filter | null>(null);

  const { trackGtagEvent } = useGTAG();

  useEffect(() => {
    setAllFilters(filters);
    setCurrentFilters(filters);
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    const currentFiltersCopy = [...allFilters];
    allFilters.forEach((filter) => {
      const selectedFilter = selectedFilters?.find(
        (item) => item.id === filter.id
      );
      const filterIndex = currentFiltersCopy.findIndex(
        (item) => item.id === selectedFilter?.id
      );
      currentFiltersCopy[filterIndex] = {
        ...filter,
        selectedValues: selectedFilter?.selectedValues
      };
    });
    setSubmittedPriceFilter(
      currentFiltersCopy?.find((filter) => filter.type === FilterTypes.Range)
    );
    setCurrentFilters(currentFiltersCopy);
  }, [JSON.stringify(selectedFilters), JSON.stringify(allFilters)]);

  const handleFilterUpdate = useCallback(
    (filter: Filter, filterValues: (string | number)[]) => {
      const currentFiltersCopy = [...currentFilters];
      const filterIndex = currentFiltersCopy.findIndex(
        (item) => item.id === filter.id
      );
      currentFiltersCopy[filterIndex] = {
        ...filter,
        selectedValues: filterValues
      };
      setCurrentFilters(currentFiltersCopy);
    },
    [currentFilters]
  );

  const removeFilter = (filter: Filter) => {
    const currentFiltersCopy = [...currentFilters];
    const filterIndex = currentFiltersCopy.findIndex(
      (item) => item.id === filter.id
    );
    currentFiltersCopy.splice(filterIndex, 1);
    setCurrentFilters(currentFiltersCopy);
    router.push(
      pageLinkGenerationMethod({
        ...router.query,
        filters: querifiedFilters(currentFiltersCopy),
        ...(textSearch && { search: textSearch }),
        page: 1
      }),
      undefined,
      { scroll: false }
    );
  };

  const isDrugsLoading = isLoading && isEmpty(drugList);

  const filterComponentData = currentFilters.filter(
    (filter) => filter.type !== FilterTypes.Range && filter.values?.length
  );

  const shownFilters = filters.filter((filter) => {
    return selectedFilters?.find(
      (selectedFilter) => selectedFilter.id === filter.id
    );
  });

  const rangeFilterData = currentFilters?.find(
    (filter) => filter.type === FilterTypes.Range
  );

  const handleFiltersSubmit = () => {
    router.push(
      pageLinkGenerationMethod({
        ...(textSearch && { search: textSearch }),
        ...router.query,
        filters: querifiedFilters(currentFilters),
        page: 1
      }),
      undefined,
      { scroll: false }
    );
    trackGtagEvent(
      'filter_apply',
      currentFilters
        .filter((filter) => filter?.selectedValues)
        .map((selectedFilter) => selectedFilter.name),
      'filter_params'
    );
    setSubmittedPriceFilter(rangeFilterData);
    if (onSubmitFilters) {
      onSubmitFilters();
    }
  };

  const handleFiltersWipe = () => {
    trackGtagEvent('filter_clear');
    router.push(
      pageLinkGenerationMethod({
        ...(textSearch && { search: textSearch }),
        ...router.query,
        filters: null
      }),
      undefined,
      { scroll: false }
    );
    setCurrentFilters(allFilters);
  };

  return (
    <div className={cn('w-full flex flex-col', containerClassName)}>
      <div className="flex flex-col bg-white pb-11 items-center justify-center">
        {isDrugsLoading ? (
          <div className="flex w-full flex-col space-y-2 pt-4 pb-6 lm:pl-4 pr-4">
            <SkeletonLoaders
              loaderList={[
                { height: 32, width: '100%' },
                { height: 32, width: '100%' },
                { height: 32, width: '100%' },
                { height: 32, width: '100%' },
                { height: 32, width: '100%' }
              ]}
            />
          </div>
        ) : (
          <>
            {!!shownFilters?.length && (
              <div className="w-full pb-6 pt-4 lm:pl-4 pr-4">
                <div className="flex flex-wrap w-full pl-0">
                  {shownFilters.map((filter) => (
                    <FilterCard
                      key={`filterCard_${filter.id}`}
                      filter={filter}
                      removeFilter={removeFilter}
                      pageLinkGenerationMethod={pageLinkGenerationMethod}
                    />
                  ))}
                </div>
                <button className="flex mt-3.5" onClick={handleFiltersWipe}>
                  <p className="fs-caption-reg text-silverChalice underline">
                    <FormattedMessage {...trns.removeAllFilters} />
                  </p>
                </button>
              </div>
            )}
            {rangeFilterData && (
              <RangeFilter
                key={`rangeFilter_${rangeFilterData.id}`}
                filter={rangeFilterData}
                handleFilterUpdate={(filterValues) =>
                  handleFilterUpdate(rangeFilterData, filterValues as number[])
                }
                submittedPriceFilter={submittedPriceFilter}
                handleSubmit={handleFiltersSubmit}
              />
            )}
            <div className="w-full pt-1 pb-6 pl-4 pr-4">
              <Accordion
                accordionList={filterComponentData.map((group) => ({
                  header: {
                    id: group.id,
                    title: group.name
                  },
                  body: [
                    <FilterComponent
                      key={`filterComponent_${group.id}`}
                      filter={group}
                      handleFilterUpdate={(filterValues) =>
                        handleFilterUpdate(group, filterValues as string[])
                      }
                    />
                  ],
                  defaultActive: !!group?.selectedValues?.length
                }))}
                renderItem={(content) => (
                  <AccordionDefaultBody content={content} />
                )}
                classNameButton={'pt-4 pb-4 border-b border-gallery'}
                renderHeader={(
                  header: AccordionHeaderContent,
                  productsList: AccordionItemContentType[],
                  active: false
                ) => (
                  <AccordionDefaultHeader
                    header={header}
                    productsList={productsList}
                    active={active}
                  />
                )}
              />
            </div>
          </>
        )}
        <ButtonSecondary
          onClick={handleFiltersSubmit}
          className="w-[180px] my-0 mx-auto"
          label={<FormattedMessage {...trns.applyFilters} />}
        />
      </div>
    </div>
  );
};

export default FiltersContainer;
